import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { InfiniteData } from '@tanstack/react-query';
import type {
  AgentApiProvidedJobPostsIndex,
  AgentApiProvidedJobPostTagsIndex,
  AgentApiProvidedJobPostTagsTagIds,
} from '@/types/serializers';
import { PROVIDED_JOB_POST_TAGS_QUERY_KEY } from './useProvidedJobPostTags';

type TagMutationParams = {
  providedJobPostId: number;
  tagId: number;
};

function updateProvidedJobPostTagsCache(
  queryClient: ReturnType<typeof useQueryClient>,
  data: AgentApiProvidedJobPostTagsTagIds,
  providedJobPostId: number
) {
  const tagMaster = queryClient.getQueryData<AgentApiProvidedJobPostTagsIndex>([
    PROVIDED_JOB_POST_TAGS_QUERY_KEY,
  ]);
  const newTags = tagMaster?.records.filter((t) => data.tag_ids.includes(t.id)) ?? [];

  queryClient.setQueriesData<InfiniteData<AgentApiProvidedJobPostsIndex>>(
    { queryKey: ['/agent/api/provided_job_posts'] },
    (oldData) => {
      if (!oldData?.pages) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map((page) => ({
          ...page,
          records: page.records.map((record) =>
            record.id === providedJobPostId ? { ...record, tags: newTags } : record
          ),
        })),
      };
    }
  );

  queryClient.invalidateQueries({
    queryKey: ['providedJobPost', providedJobPostId],
  });
}

export function useProvidedJobPostChain() {
  const token = (document.getElementsByName('csrf-token')[0] as HTMLMetaElement).content;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ providedJobPostId, tagId }: TagMutationParams) => {
      const response = await fetch(`/agent/api/provided_job_post_tags/${providedJobPostId}/chain`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': token,
        },
        body: JSON.stringify({ provided_job_post_tag: { id: tagId } }),
      });
      if (!response.ok) {
        throw new Error(`Failed to chain tag: ${response.status}`);
      }
      const data: AgentApiProvidedJobPostTagsTagIds = await response.json();
      return data;
    },
    onSuccess: (data, variables) => {
      updateProvidedJobPostTagsCache(queryClient, data, variables.providedJobPostId);
    },
  });
}

export function useProvidedJobPostUnchain() {
  const token = (document.getElementsByName('csrf-token')[0] as HTMLMetaElement).content;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ providedJobPostId, tagId }: TagMutationParams) => {
      const response = await fetch(
        `/agent/api/provided_job_post_tags/${providedJobPostId}/unchain`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-CSRF-Token': token,
          },
          body: JSON.stringify({ provided_job_post_tag: { id: tagId } }),
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to unchain tag: ${response.status}`);
      }
      const data: AgentApiProvidedJobPostTagsTagIds = await response.json();
      return data;
    },
    onSuccess: (data, variables) => {
      updateProvidedJobPostTagsCache(queryClient, data, variables.providedJobPostId);
    },
  });
}
